<template>
  <meta name="viewport" content="width=device-width, initial-scale=1, minimum-scale=1, maximum-scale=3, user-scalable=yes">
  <div
    class="container"
    v-loading="isLoading"
    element-loading-text="로딩 중..."
  >
    <!-- 상태별 메시지 -->
    <div v-if="status" class="status-message">
      <el-alert
        v-if="status === 'no_code'"
        type="error"
        :title="errorMessage || '유효하지 않은 접근입니다.'"
        show-icon
        class="alert"
      />
      <el-alert
        v-if="status === 'success'"
        type="success"
        :title="'정보가 성공적으로 제출되었습니다.'"
        show-icon
        class="alert"
      />
      <el-alert
        v-if="status === 'already_submitted'"
        type="warning"
        :title="errorMessage || '이미 제출된 정보입니다.'"
        show-icon
        class="alert"
      />
      <el-alert
        v-if="status === 'not_found'"
        type="error"
        :title="errorMessage || '제출한 심카드 정보를 조회할 수 없습니다.'"
        show-icon
        class="alert"
      />
      <el-alert
        v-if="status === 'server_error'"
        type="error"
        :title="errorMessage || '서버 오류가 발생했습니다.'"
        show-icon
        class="alert"
      />
      <el-alert
        v-if="status === 'error'"
        type="error"
        :title="errorMessage || '오류가 발생했습니다. 다시 시도해 주세요.'"
        show-icon
        class="alert"
      />
    </div>

    <div v-if="productInfo" class="wrap">

      <!-- 상품 이름 -->
      <h1 class="product-name">{{ productInfo.name }}</h1>

      <!-- 제출 방법 설명 -->
      <div class="submission-guide">
        <el-card class="submission-card">
          <p class="submission-text">
            이 상품은 개통이 필요한 상품입니다.<br>
            개통을 위해 <strong>심카드 번호</strong>와 <strong>개통 희망일</strong>을 입력한 후,<br class="mb-dn"> 아래의 제출하기 버튼을 눌러 주세요.
          </p>
          <p class="submission-text-2">
            제출하신 정보는 안전하게 처리되며, <br class="mb-dn">정확한 개통을 위해 입력 내용을 신중히 확인해 주세요.
          </p>
        </el-card>
      </div>

      <!-- 심카드 샘플 이미지 -->
      <div class="image-section">
        <img
          :src="`https://jupiter.planett.co.kr${productInfo.image}`"
          alt="Product Image"
          class="product-image"
        />
      </div>

      <!-- 심카드 번호 입력 -->
      <div class="form-section">
        <h2 class="form-section-title"><strong>STEP.1</strong>&emsp;심카드 번호 입력</h2>
        <p class="hint-text">
          심카드 번호는 <strong>심카드 뒷면</strong>에서 확인할 수 있습니다. <br>
          <strong>{{ productInfo.prefix }}</strong>로 시작하며, 총 <strong>{{ productInfo.length }}자리</strong>로 구성된 번호입니다. 
          <!-- <span class="image-reference">(위 샘플 이미지를 참고하세요)</span> -->
        </p>
        <p class="hint-text">
          (번호 끝에 문자 'F'가 포함된 경우, 'F'를 포함한 전체 번호를 순서대로 입력해 주세요.)
        </p>
        <el-form-item label="심카드 번호" class="input-style">
          <el-input
            v-model="iccid"
            :maxlength="computedMaxLength"
            placeholder="심카드 번호 전체 입력"
            @input="handleIccidInput"
          />
        </el-form-item>
        <p v-if="errorMessage && iccid" class="error-message-text">
          <span class="error-icon">!</span> {{ errorMessage }}
        </p>
      </div>

      <!-- 개통 희망일 선택 -->
      <div class="form-section">
        <h2 class="form-section-title"><strong>STEP.2</strong>&emsp;개통희망일 선택</h2>
        <p class="hint-text">
          개통희망일은 <strong>{{ localizedTimezone }} 시간 기준</strong>으로 <strong>{{ todayInTimezone }}</strong>의 다음날인 <br class="mb-dn"><strong>{{ firstSelectableDate }}부터 선택</strong>할 수 있습니다.
        </p>
        <el-form-item label="개통희망일" class="input-style">
          <el-date-picker
            v-model="activationDate"
            type="date"
            value-format="YYYY-MM-DD"
            placeholder="날짜 선택"
            :disabled-date="disablePastDates"
            style="width: 100%"
          />
        </el-form-item>
      </div>

      <!-- 제출 버튼 -->
      <el-form-item>
        <el-button
          type="primary"
          :disabled="!isFormValid || !isIccidValid || isLoading"
          @click="submitProductInfo"
        >
          제출하기
        </el-button>
      </el-form-item>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

dayjs.extend(utc);
dayjs.extend(timezone);

const timezoneMap = {
  "Asia/Seoul": "대한민국 서울",
  "Europe/London": "영국 런던",
  "Europe/Paris": "프랑스 파리",
  "Asia/Tokyo": "일본 도쿄",
  "Asia/Shanghai": "중국 상하이",
  "Asia/Hong_Kong": "홍콩",
  "Asia/Ho_Chi_Minh": "베트남 시간",
  "Asia/Bangkok": "태국 시간",
  // 추가 필요 시 여기에 타임존 추가
  "America/New_York": "미국 뉴욕",
  "America/Los_Angeles": "미국 LA",
  "US/Eastern": "미국 동부",
  "US/Central": "미국 중부",
  "US/Pacific": "미국 서부",
  "US/Mountain": "미국 산악",
  "US/Hawaii": "하와이",
};

export default {
  data() {
    return {
      previousIccid: "",
      productInfo: null,
      activationDate: "",
      iccid: "",
      status: "",
      errorMessage: "",
      isLoading: false,
      isIccidValid: false,
      timezone: "UTC", // 기본 Time Zone
    };
  },
  computed: {
    isFormValid() {
      return (
        this.activationDate &&
        this.iccid.length === this.productInfo?.length
      );
    },
    computedMaxLength() {
      return this.productInfo?.length || 20;
    },
    todayInTimezone() {
      return dayjs().tz(this.productInfo.time_zone).format("YYYY-MM-DD");
    },
    localizedTimezone() {
      return timezoneMap[this.productInfo.time_zone] || this.productInfo.time_zone;
    },
    firstSelectableDate() {
      return dayjs().tz(this.productInfo.time_zone).add(1, "day").format("YYYY-MM-DD");
    },
  },
  watch: {
    "$route.query": "handleCodeChange",
  },
  methods: {
    async handleCodeChange() {
      const code = this.$route?.query?.code;
      if (!code) {
        this.setNoCodeError();
        return;
      }

      this.isLoading = true;
      try {
        const response = await axios.post(
          "https://jupiter.planett.co.kr/api/coupang/activate/validate-code",
          { code }
        );

        this.productInfo = response.data;
        this.timezone = this.productInfo.time_zone || "UTC"; // Timezone 설정
        this.status = "";
      } catch (error) {
        this.setError(error.response?.data?.message || "코드 검증에 실패했습니다.");
      } finally {
        this.isLoading = false;
      }
    },
    disablePastDates(date) {
      const today = dayjs().tz(this.productInfo.time_zone).startOf("day");
      const testDate = dayjs(date).startOf("day");
      return testDate.isSameOrBefore(today); // 오늘 포함 이전 날짜 비활성화
    },
    async submitProductInfo() {
      this.isLoading = true;
      this.status = "";
      this.errorMessage = "";

      try {
        const response = await axios.post(
          "https://jupiter.planett.co.kr/api/coupang/activate/submit",
          {
            code: this.productInfo.code,
            activationDate: this.activationDate,
            iccid: this.iccid,
          }
        );

        if (response.status === 200 && response.data.success) {
          this.status = "success";
          this.errorMessage = "";
        }
      } catch (error) {
        const status = error.response?.status;
        const errorData = error.response?.data;

        if (status === 400 && errorData?.error === "Already processed") {
          this.status = "already_submitted";
          this.errorMessage = "이미 제출된 정보입니다.";
        } else if (status === 404 && errorData?.error === "No products") {
          this.status = "not_found";
          this.errorMessage = "제출한 심카드 정보를 조회할 수 없습니다.";
        } else if (status === 500) {
          this.status = "server_error";
          this.errorMessage =
            errorData?.error || "서버 오류가 발생했습니다. 다시 시도해 주세요.";
        } else {
          this.status = "error";
          this.errorMessage =
            errorData?.error || "알 수 없는 오류가 발생했습니다.";
        }
      } finally {
        this.isLoading = false;
      }
    },
    setNoCodeError() {
      this.status = "no_code";
      this.errorMessage = "유효하지 않은 접근입니다.";
      this.productInfo = null;
    },
    setError(message) {
      this.errorMessage = message;
      this.status = "error";
      this.productInfo = null;
    },
    handleIccidInput(value) {
      // 중복 호출 방지
      if (this.previousIccid === value) return;

      // 소문자 f 또는 한글 ㄹ을 대문자 F로 변경
      this.iccid = value
        .replace(/[^0-9fFㄹ]/g, "") // 숫자, f, F, ㄹ만 허용
        .replace(/f|ㄹ/g, "F"); // 소문자 f 및 한글 ㄹ을 대문자 F로 변환

      // 유효성 검사 실행
      this.validateIccid();

      // 최종적으로 업데이트된 iccid 값을 previousIccid에 저장
      this.previousIccid = this.iccid;
    },
    validateIccid() {
      if (!this.productInfo?.prefix || !this.productInfo?.length) {
        this.isIccidValid = false;
        this.errorMessage = "심카드 번호 정보를 불러오지 못했습니다.";
        return;
      }

      const prefix = this.productInfo.prefix;
      const length = this.productInfo.length;

      // ICCID 길이 검사
      if (this.iccid.length !== length) {
        this.isIccidValid = false;
        this.errorMessage = `심카드 번호는 ${length}자리여야 합니다.`;
        return;
      }

      // ICCID 시작 부분(prefix) 검사
      if (!this.iccid.startsWith(prefix)) {
        this.isIccidValid = false;
        this.errorMessage = `심카드 번호는 ${prefix}로 시작해야 합니다.`;
        return;
      }

      // Luhn 알고리즘 검사: 마지막 문자가 F이면 제외
      const normalizedIccid = this.iccid.endsWith("F")
        ? this.iccid.slice(0, -1) // 마지막 F 제거
        : this.iccid;

      const isValid = this.checkLuhn(normalizedIccid);
      this.isIccidValid = isValid;
      this.errorMessage = isValid ? "" : "유효하지 않은 심카드 번호입니다.";
    },
    checkLuhn(cardNo) {
      const nDigits = cardNo.length;
      let nSum = 0;
      let isSecond = false;

      for (let i = nDigits - 1; i >= 0; i--) {
        let d = parseInt(cardNo[i], 10);
        if (isSecond) {
          d *= 2;
          nSum += Math.floor(d / 10) + (d % 10);
        } else {
          nSum += d;
        }
        isSecond = !isSecond;
      }

      return nSum % 10 === 0;
    },
  },
  mounted() {
    this.handleCodeChange();
  },
};
</script>

<style>
.mb-dn{
  display: none;
}
/* 공통 스타일 */
body {
  font-family: "Pretendard Variable", Pretendard, -apple-system, BlinkMacSystemFont, system-ui, Roboto, "Helvetica Neue", "Segoe UI", "Apple SD Gothic Neo", "Noto Sans KR", "Malgun Gothic", "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", sans-serif;
}

.container {
  margin: 0 auto;
  padding: 40px;
  max-width: 850px;
  background-color: #efefef;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.hint-text {
  font-size: 18px;
  color: #000000;
  line-height: 26px;
  font-weight: 400;
  margin: 0;
}

.hint-text strong {
  font-size: 18px;
  color: #000000;
  line-height: 24px;
  font-weight: 700;
}

/* 제출 방법 스타일 */
.input-style{
  width: 500px;
  height: 45px;
  margin-top: 30px;
}
.el-form-item{
    width: 100% !important;
  }

.el-form-item__content{
  align-items: flex-start;
}

.el-form-item__label{
  width: 120px;
  height: 46px !important;
  border-radius: 8px 0 0 8px;
  background-color: #186EF8;
  color: #fff;
  line-height: 45px;
  font-size:16px;
  font-weight: 600;
  text-align: center;
  padding: 0;
  justify-content: center;
}
.el-input__wrapper{
  border: none;
  box-shadow: none;
  align-items: center;
  border: solid 0.5px #cecece;
  border-radius: 0 8px 8px 0;
  height: 44px;
  margin-left: -1px;
}


.submission-card {
  max-width: 750px;
  background-color: #444444;
  padding: 0 40px;
  text-align: center;
  border-radius: 0;
  margin: 0 auto;
}

.submission-text {
  font-size: 20px;
  color: #fff;
  line-height: 24px;
  margin: 10px 0 20px;
  font-weight: 400;
}

p.submission-text strong {
  color: #FFEA00; /* 강조된 텍스트 */
  font-weight: 600;
}

.submission-text-2 {
  font-size: 17px;
  color: #fff;
  line-height: 19px;
  font-weight: 400;
}


/* 메시지 스타일 */
.alert {
  margin-bottom: 20px;
}

/* 상품 이름 스타일 */
.product-name {
  color: #000000;
  font-size: 28px;
  font-weight: 800;
  text-align: center;
  line-height: 35px;
  word-break: break-word;
  margin-bottom: 30px;
}

/* 섹션 스타일 */
.form-section {
  max-width: 750px;
  margin-bottom: 20px;
  padding: 20px 30px 30px;
  background-color: #fdfdfd;
}

.form-section-title {
  font-size: 20px;
  font-weight: 700;
  color: #000000;
  margin-bottom: 20px;
  line-height: 25px;
}
h2.form-section-title strong{
  font-size: 18px;
  font-weight: 900;
  line-height: 25px;
}

/* 이미지 섹션 */
.image-section {
  display: flex;
  justify-content: center;
  align-items: center;
  margin: 25px 0;
}

.product-image {
  max-width: 100%;
  max-height: 300px;
  /* border-radius: 8px; */
  /* transition: transform 0.2s ease-in-out; */
}

.product-image:hover {
  transform: scale(1.05);
}

.image-reference {
  color: #007bff; /* 강조를 위한 파란색 */
  font-weight: bold;
  cursor: pointer; /* 사용자가 클릭 가능하다고 느낄 수 있도록 */
}

.image-reference:hover {
  text-decoration: underline; /* 사용자가 링크처럼 느낄 수 있도록 */
}

/* 입력 스타일 */
.iccid-form-item {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.iccid-input-container {
  display: flex;
  align-items: center;
  gap: 10px;
}

.iccid-prefix {
  font-weight: bold;
  font-size: 16px;
  color: #555;
  white-space: nowrap;
}

.iccid-input {
  flex: 1;
  padding: 8px;
  border: 1px solid #ccc;
  border-radius: 5px;
  width: 100%;
}

/* 버튼 스타일 */
.el-button {
  width: 100%;
  height: 60px;
  background-color: #186EF8;
  color: white;
  font-size: 18px;
  line-height: 60px;
  padding: 10px;
  border-radius: 8px;
  text-align: center;
  cursor: pointer;
  transition: background-color 0.3s ease;
  border: none;
}

.el-button:disabled {
  background-color: #ddd;
  color: #aaa;
  cursor: not-allowed;
}

.el-button:hover {
  background-color: #0056b3;
}

/* 에러 메시지 스타일 */
.error-message-text {
  color: #e74c3c;
  font-size: 14px;
  margin: 0 0 10px;
  display: flex;
  align-items: center;
}

.error-icon {
  width: 17px;
  height: 17px;
  border-radius: 50px;
  background-color: #e74c3c;
  color: #fff;
  font-weight: 700;
  font-size: 13px;
  line-height: 17px;
  margin-right: 5px;
  text-align: center;
}



/* 반응형 디자인 */
@media (max-width: 768px) {
  .mb-dn{
    display: block;
  }
  .container {
    padding: 15px;
  }

  .submission-card {
  max-width: 600px;
  padding: 0 25px;
}


  .product-name {
    font-size: clamp(18px, 4.5vw, 24px);
  }

  .form-section {
    max-width: 600px;
    padding: 15px 25px 30px;
  }

  .form-section-title {
    font-size: 18px;
  }

  .product-image {
    max-height: 270px;
  }

  .el-button {
    font-size: 18px;
  }

  .iccid-input-container {
    flex-direction: column;
    gap: 5px;
  }
}

@media (max-width: 480px) {
  .mb-dn{
    display: none;
  }
  .wrap{
    max-width: 430px;
    width: 97%;
  }
  .submission-card{
    padding: 0;
  }
  .submission-text {
    font-size: 15px;
    line-height: 22px;
  }
  .submission-text-2{
    font-size: 14px;
  }
  .product-name {
    padding: 0 15px;
    font-size: 23px;
    margin-bottom: 20px;
    line-height: 30px;
  }
  .form-section {
    padding: 15px 15px 30px;
  }

  .form-section-title {
    font-size: 18px;
  }
  .hint-text, .hint-text strong{
    font-size: 15px;
    line-height: 20px;
  }

  .product-image {
    max-height: 200px;
  }

  .input-style {
    max-width: 450px;
  }

  .el-form-item__label {
    width: 90px;
    font-size: 14px;
  }

  .el-input__inner{
    font-size: 13px;
  }
  .el-input__wrapper{
    max-width: 250px;
  }

  .error-message-text{
    font-size: 14px;
  }

}
</style>
